import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Button from '../../../components/ui/Button';

const GroupProfitabilityPanel = ({ groups, onExport }) => {
  const { currentLanguage, t } = useLanguage();

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      // Arabic formatting with Arabic numerals
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      // French formatting
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 2
      })?.format(amount);
    }
  };

  const getMarginColor = (margin) => {
    if (margin >= 20) return 'text-success';
    if (margin >= 10) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm">
      <div className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 md:mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            {t('financial.groupProfitability')}
          </h3>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={onExport}
            className="w-full sm:w-auto"
          >
            {t('financial.export')}
          </Button>
        </div>
        <div className="space-y-4">
          {groups?.map((group) => (
            <div
              key={group?.id}
              className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-smooth"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 lg:gap-4 mb-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">{group?.name}</h4>
                  <p className="caption text-muted-foreground">
                    {group?.pilgrims} {t('financial.pilgrims')}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ChevronRight"
                  iconPosition="right"
                  className="w-full sm:w-auto"
                >
                  {t('financial.viewDetails')}
                </Button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="caption text-muted-foreground mb-1">
                    {t('financial.revenue')}
                  </p>
                  <p className="text-lg font-semibold text-foreground data-text">
                    {formatCurrency(group?.revenue)}
                  </p>
                </div>
                
                <div>
                  <p className="caption text-muted-foreground mb-1">
                    {t('financial.costs')}
                  </p>
                  <p className="text-lg font-semibold text-foreground data-text">
                    {formatCurrency(group?.costs)}
                  </p>
                </div>
                
                <div>
                  <p className="caption text-muted-foreground mb-1">
                    {t('financial.profit')}
                  </p>
                  <p className="text-lg font-semibold text-success data-text">
                    {formatCurrency(group?.profit)}
                  </p>
                </div>
                
                <div>
                  <p className="caption text-muted-foreground mb-1">
                    {t('financial.margin')}
                  </p>
                  <p className={`text-lg font-semibold ${getMarginColor(group?.margin)} data-text`}>
                    {group?.margin}%
                  </p>
                </div>
              </div>

              <div className="mt-4 bg-background rounded-md overflow-hidden">
                <div className="h-2 bg-muted">
                  <div
                    className="h-full bg-success transition-all duration-500"
                    style={{ width: `${group?.margin}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GroupProfitabilityPanel;